import { create } from 'zustand';
import { storage, STORAGE_KEYS } from '@utils/storage';
import { authService } from '@services/authService';
import { getErrorMessage } from '@services/api';
import { useCartStore } from './cartStore';
import type { AuthTokens, User, UserRole } from '@/types';

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  selectedRole: UserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string, role: UserRole) => Promise<void>;
  logout: () => Promise<void>;
  clearLocalSession: () => void;
  loadFromStorage: () => void;
  setSelectedRole: (role: UserRole) => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tokens: null,
  selectedRole: storage.get<UserRole>(STORAGE_KEYS.SELECTED_ROLE),
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (username, password, role) => {
    set({ isLoading: true, error: null });
    try {
      const { user, tokens } = await authService.login({ username, password, role });
      storage.set(STORAGE_KEYS.AUTH_TOKENS, tokens);
      storage.set(STORAGE_KEYS.USER_DATA, user);
      storage.set(STORAGE_KEYS.SELECTED_ROLE, role);
      set({
        user,
        tokens,
        selectedRole: role,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      try {
        await useCartStore.getState().syncFromBackend();
      } catch {
        // cart sync optional on login
      }
    } catch (error) {
      set({ isLoading: false, error: getErrorMessage(error, 'Giriş başarısız') });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
    } catch {
      // ignore logout errors
    } finally {
      storage.remove(STORAGE_KEYS.AUTH_TOKENS);
      storage.remove(STORAGE_KEYS.USER_DATA);
      useCartStore.getState().reset();
      set({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  clearLocalSession: () => {
    storage.remove(STORAGE_KEYS.AUTH_TOKENS);
    storage.remove(STORAGE_KEYS.USER_DATA);
    useCartStore.getState().reset();
    set({ user: null, tokens: null, isAuthenticated: false, isLoading: false, error: null });
  },

  loadFromStorage: () => {
    const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
    const user = storage.get<User>(STORAGE_KEYS.USER_DATA);
    const selectedRole = storage.get<UserRole>(STORAGE_KEYS.SELECTED_ROLE);
    if (tokens && user) {
      set({ tokens, user, selectedRole, isAuthenticated: true });
    }
  },

  setSelectedRole: (role) => {
    storage.set(STORAGE_KEYS.SELECTED_ROLE, role);
    set({ selectedRole: role });
  },

  refreshUser: async () => {
    try {
      const user = await authService.getProfile();
      storage.set(STORAGE_KEYS.USER_DATA, user);
      set({ user });
    } catch {
      // ignore
    }
  },

  clearError: () => set({ error: null }),
}));

useAuthStore.getState().loadFromStorage();

if (typeof window !== 'undefined') {
  window.addEventListener('auth:clear', () => {
    useAuthStore.getState().clearLocalSession();
  });

  window.addEventListener('auth:tokens', (event: Event) => {
    const detail = (event as CustomEvent).detail as { tokens?: AuthTokens } | undefined;
    const tokens = detail?.tokens;
    if (!tokens) return;
    useAuthStore.setState((prev) => ({
      ...prev,
      tokens,
      user: prev.user ?? storage.get<User>(STORAGE_KEYS.USER_DATA),
      isAuthenticated: Boolean((prev.user ?? storage.get<User>(STORAGE_KEYS.USER_DATA))?.id),
    }));
  });
}
